/**
 * Utility functions for generating myopia report graphs
 * These functions generate graphs that match the Kotlin code output exactly
 */

import { Chart, registerables } from "chart.js";

// Register Chart.js components
Chart.register(...registerables);

/**
 * Generate Growth Curve graph (Graph 1a)
 * This matches the Kotlin EyeLengthEstimator.getChartData output
 * Plots both LE (left eye) and RE (right eye) with labels "LE" and "RE".
 */
export const generateGrowthCurveGraph = (submittedData, gender = "MALE") => {
  return new Promise((resolve, reject) => {
    let container = null;
    try {
      // Create a hidden container for the chart
      container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "-9999px";
      container.style.width = "1000px";
      container.style.height = "800px";
      document.body.appendChild(container);

      const canvas = document.createElement("canvas");
      canvas.width = 1000;
      canvas.height = 800;
      container.appendChild(canvas);
      const ctx = canvas.getContext("2d");

      // Reference tables from EyeLengthSdk.kt
      const TABLES = {
        MALE: {
          ETM: {
            6: 22.61,
            7: 22.79,
            8: 22.95,
            9: 23.1,
            10: 23.23,
            11: 23.34,
            12: 23.44,
            13: 23.52,
            14: 23.59,
            15: 23.65,
            16: 23.7,
            17: 23.74,
            18: 23.78,
            19: 23.81,
            20: 23.83,
            21: 23.84
          },
          LRM: {
            6: 23.2,
            7: 23.48,
            8: 23.75,
            9: 24.03,
            10: 24.1,
            11: 24.17,
            12: 24.25,
            13: 24.32,
            14: 24.4,
            15: 24.47,
            16: 24.495,
            17: 24.52,
            18: 24.54,
            19: 24.56,
            20: 24.58,
            21: 24.6
          },
          MRM: {
            6: 23.65,
            7: 23.99,
            8: 24.31,
            9: 24.6,
            10: 24.85,
            11: 25.07,
            12: 25.27,
            13: 25.43,
            14: 25.57,
            15: 25.7,
            16: 25.81,
            17: 25.89,
            18: 25.94,
            19: 25.97,
            20: 25.99,
            21: 26.0
          },
          HRM: {
            6: 25.0,
            7: 25.36,
            8: 25.7,
            9: 26.0,
            10: 26.25,
            11: 26.47,
            12: 26.67,
            13: 26.83,
            14: 26.99,
            15: 27.13,
            16: 27.24,
            17: 27.32,
            18: 27.4,
            19: 27.46,
            20: 27.5,
            21: 27.52
          },
          EXTRM: Object.fromEntries(
            Array.from({ length: 16 }, (_, i) => [i + 6, 29.0])
          )
        },
        FEMALE: {
          ETM: {
            6: 22.0,
            7: 22.22,
            8: 22.42,
            9: 22.59,
            10: 22.74,
            11: 22.87,
            12: 22.99,
            13: 23.07,
            14: 23.14,
            15: 23.21,
            16: 23.28,
            17: 23.34,
            18: 23.39,
            19: 23.43,
            20: 23.46,
            21: 23.48
          },
          LRM: {
            6: 22.68,
            7: 22.95,
            8: 23.22,
            9: 23.49,
            10: 23.56,
            11: 23.64,
            12: 23.72,
            13: 23.79,
            14: 23.86,
            15: 23.93,
            16: 23.96,
            17: 23.98,
            18: 24.0,
            19: 24.02,
            20: 24.04,
            21: 24.06
          },
          MRM: {
            6: 23.11,
            7: 23.48,
            8: 23.8,
            9: 24.04,
            10: 24.27,
            11: 24.49,
            12: 24.67,
            13: 24.84,
            14: 24.98,
            15: 25.1,
            16: 25.21,
            17: 25.31,
            18: 25.4,
            19: 25.44,
            20: 25.47,
            21: 25.5
          },
          HRM: {
            6: 24.0,
            7: 24.37,
            8: 24.68,
            9: 24.95,
            10: 25.2,
            11: 25.44,
            12: 25.66,
            13: 25.86,
            14: 26.06,
            15: 26.25,
            16: 26.42,
            17: 26.57,
            18: 26.7,
            19: 26.77,
            20: 26.81,
            21: 26.84
          },
          EXTRM: Object.fromEntries(
            Array.from({ length: 16 }, (_, i) => [i + 6, 28.0])
          )
        }
      };

      const genderKey = gender === "MALE" ? "MALE" : "FEMALE";
      const table = TABLES[genderKey];

      // Prepare data
      const ageRange = Array.from({ length: 13 }, (_, i) => i + 6); // 6 to 18

      // Real patient data: LE (left) and RE (right) axial length
      // Only show current exam (first item in array) - not historical data
      const currentExam = submittedData.length > 0 ? submittedData[0] : null;

      const realDataLE =
        currentExam && currentExam.l !== null && currentExam.l !== undefined
          ? [{ x: currentExam.age, y: currentExam.l }]
          : [];
      const realDataRE =
        currentExam && currentExam.r !== null && currentExam.r !== undefined
          ? [{ x: currentExam.age, y: currentExam.r }]
          : [];

      // Calculate minimum and maximum y values from all data sources (real data and severity bands)
      const allYValues = [
        ...realDataLE.map(d => d.y),
        ...realDataRE.map(d => d.y)
      ];

      // Add minimum and maximum values from severity bands
      const etmMinValue = Math.min(...Object.values(table.ETM));
      const extrmMaxValue = Math.max(...Object.values(table.EXTRM));
      allYValues.push(etmMinValue);
      allYValues.push(extrmMaxValue);

      const minDataValue = allYValues.length > 0 ? Math.min(...allYValues) : 22;
      const maxDataValue = allYValues.length > 0 ? Math.max(...allYValues) : 29;

      // Set y-axis min to be 0.5 below the lowest value, but not less than 20
      const yAxisMin = Math.max(20, Math.floor((minDataValue - 0.5) * 10) / 10);
      // Set y-axis max to be 0.5 above the highest value, but not less than 29
      const yAxisMax = Math.max(29, Math.ceil((maxDataValue + 0.5) * 10) / 10);

      // Severity bands data - order from bottom to top (ETM to EXTRM)
      // This matches Kotlin: listOf(Severity.ETM, Severity.LRM, Severity.MRM, Severity.HRM, Severity.EXTRM)
      // In Chart.js, datasets are drawn in array order (first = bottom layer)
      // So we build: [ETM (green, bottom), LRM (yellow), MRM (orange), HRM (red), EXTRM (dark red, top)]
      const severityOrder = ["ETM", "LRM", "MRM", "HRM", "EXTRM"];
      const datasets = [];

      // Colors array - order matches severityOrder (bottom to top)
      const severityColors = [
        "#4C7B31", // Green - ETM (bottom, index 0)
        "#F1D548", // Yellow - LRM (index 1)
        "#ECAA56", // Orange - MRM (index 2)
        "#E83423", // Red - HRM (index 3)
        "#B72619" // Dark red - EXTRM (top, index 4)
      ];

      // Build datasets in order from bottom to top for proper stacking
      severityOrder.forEach((severity, index) => {
        const data = ageRange.map(age => {
          const currentValue = table[severity][age] || table[severity][21];
          return {
            x: age,
            y: currentValue
          };
        });

        datasets.push({
          label: severity,
          data,
          borderWidth: 0,
          fill: index === 0 ? true : "-1", // ETM fills from origin, others fill from previous dataset
          backgroundColor: severityColors[index],
          pointRadius: 0,
          order: index // Lower order draws first (on bottom)
        });
      });

      // Add real patient data: LE and RE as separate series
      // Use scatter type to avoid vertical lines connecting points at same age
      if (realDataLE.length > 0) {
        datasets.push({
          label: "Real Patient Data LE",
          data: realDataLE,
          borderWidth: 3,
          borderColor: "#CBE8F8",
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          showLine: false // Don't draw lines between points
        });
      }
      if (realDataRE.length > 0) {
        datasets.push({
          label: "Real Patient Data RE",
          data: realDataRE,
          borderWidth: 3,
          borderColor: "#CBE8F8",
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          showLine: false // Don't draw lines between points
        });
      }

      // Create chart
      // Note: datasets are already in correct order (ETM first/bottom, EXTRM last/top)
      // Don't reverse - Chart.js draws in array order, and fill: "-1" references depend on array position
      const chart = new Chart(ctx, {
        type: "line",
        data: {
          datasets: datasets
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          animation: false, // Disable animation for faster rendering
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              enabled: false
            }
          },
          scales: {
            x: {
              type: "linear",
              position: "bottom",
              min: 6,
              max: 18,
              title: {
                display: true,
                text: "Age (Years)",
                font: { size: 25 }
              },
              ticks: {
                font: { size: 20 }
              },
              grid: {
                display: false
              }
            },
            y: {
              min: yAxisMin,
              max: yAxisMax,
              title: {
                display: true,
                text: "Axial Length (mm)",
                font: { size: 25 }
              },
              ticks: {
                font: { size: 20 }
              },
              grid: {
                display: true
              }
            }
          }
        }
      });

      // Wait for chart to render, then draw LE and RE labels on patient data points
      const drawEyeLabels = (label, data, meta) => {
        if (!data.length || !meta) return;
        data.forEach((_, idx) => {
          const pointMeta = meta.data[idx];
          if (pointMeta) {
            const x = pointMeta.x;
            const y = pointMeta.y;
            ctx.fillStyle = "#CCE8F8";
            ctx.beginPath();
            ctx.arc(x, y, 20, 0, 2 * Math.PI);
            ctx.fill();
            ctx.strokeStyle = "#FFFFFF";
            ctx.lineWidth = 5;
            ctx.beginPath();
            ctx.arc(x, y, 20, 0, 2 * Math.PI);
            ctx.stroke();
            ctx.fillStyle = "#000000";
            ctx.font = "bold 20px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(label, x, y);
          }
        });
      };

      setTimeout(() => {
        const idxLE = chart.data.datasets.findIndex(
          ds => ds.label === "Real Patient Data LE"
        );
        const idxRE = chart.data.datasets.findIndex(
          ds => ds.label === "Real Patient Data RE"
        );
        if (realDataLE.length > 0 && idxLE >= 0) {
          drawEyeLabels("LE", realDataLE, chart.getDatasetMeta(idxLE));
        }
        if (realDataRE.length > 0 && idxRE >= 0) {
          drawEyeLabels("RE", realDataRE, chart.getDatasetMeta(idxRE));
        }

        const imageData = canvas.toDataURL("image/png");
        chart.destroy(); // Clean up
        if (container && container.parentNode) {
          document.body.removeChild(container); // Remove from DOM
        }
        resolve(imageData);
      }, 500);
    } catch (error) {
      if (container && container.parentNode) {
        document.body.removeChild(container);
      }
      reject(error);
    }
  });
};

/**
 * Generate Predictive Curve graph (Graph 1b)
 * - When opts.currentRef is provided: use current exam as reference — prescriptionAge=currentRef.age,
 *   startPrescription=that eye from currentRef, useL from currentRef (getEyeLabel-style). Treatment curves project from there.
 * - Otherwise: Kotlin/GraphUtil behavior (min-age row for start, getEyeLabel from last).
 * @param {Array<{age:number, l:number|null, r:number|null, rate:number}>} predictiveData
 * @param {{ currentRef?: { age: number, l: number|null, r: number|null }, eyeCode?: string, selectedItems?: { includeRealPatientData?: boolean, selectedTreatments?: number[] } }} [opts]
 */
export const generatePredictiveCurveGraph = (predictiveData, opts = {}) => {
  const opt =
    opts && typeof opts === "object" && !Array.isArray(opts) ? opts : {};
  const currentRef = opt.currentRef;
  const eyeCode = opt.eyeCode != null ? opt.eyeCode : null;
  const selectedItems = opt.selectedItems || {
    includeRealPatientData: true,
    selectedTreatments: [30, 40, 50, 60, 70, 0] // All selected by default
  };
  return new Promise((resolve, reject) => {
    let container = null;
    try {
      // Create a hidden container for the chart
      container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "-9999px";
      container.style.width = "1000px";
      container.style.height = "800px";
      document.body.appendChild(container);

      const canvas = document.createElement("canvas");
      canvas.width = 1000;
      canvas.height = 800;
      container.appendChild(canvas);
      const ctx = canvas.getContext("2d");

      // Calculate progression using MyopiaProgressionCalculator logic
      const DEFAULT_PROG = -0.04;
      const MIN_PROGRESSION_AGE = 5;
      const PROG_CONST_X3 = 0.0022;
      const PROG_CONST_X2 = -0.0403;
      const PROG_CONST_X1 = 0.2859;
      const PROG_CONST_X0 = -1.3244;

      const PROG_TABLE = [
        0.0,
        0.0,
        0.0,
        0.0,
        0.0,
        0.0,
        -1.0766,
        -0.8962,
        -0.77,
        -0.6848,
        -0.6274,
        -0.5846,
        -0.5432,
        -0.49,
        -0.4118,
        -0.2954,
        -0.24,
        -0.2,
        -0.16,
        -0.13,
        -0.1,
        -0.08,
        -0.06,
        -0.05
      ];

      const getProgressionInternal = (age, slowing) => {
        if (age > 15) {
          if (age >= PROG_TABLE.length) return slowing * DEFAULT_PROG;
          return slowing * PROG_TABLE[age];
        }
        if (age < MIN_PROGRESSION_AGE) return 0.0;
        const x = age - 5;
        return (
          slowing *
          (Math.pow(x, 3) * PROG_CONST_X3 +
            Math.pow(x, 2) * PROG_CONST_X2 +
            x * PROG_CONST_X1 +
            PROG_CONST_X0)
        );
      };

      const getPrescriptionAtAge = (
        startPrescription,
        startAge,
        curAge,
        slowing,
        treatmentAccelMultiplier,
        patientRateMultiplier
      ) => {
        let newPrescription = startPrescription;
        if (startAge === curAge) return startPrescription;

        for (let age = startAge; age < curAge; age++) {
          if (age < MIN_PROGRESSION_AGE) {
            newPrescription += 0.0;
          } else if (age < PROG_TABLE.length) {
            newPrescription +=
              getProgressionInternal(age, slowing) *
              treatmentAccelMultiplier *
              patientRateMultiplier;
          } else {
            newPrescription += slowing * DEFAULT_PROG;
          }
        }
        return newPrescription;
      };

      if (predictiveData.length === 0) {
        reject(new Error("No predictive data provided"));
        return;
      }

      const byAge = [...predictiveData].sort((a, b) => a.age - b.age);
      const rdAges = [...new Set(predictiveData.map(d => d.age))].sort(
        (a, b) => a - b
      );

      // Use current exam as reference when currentRef is provided (LE/RE = current patient values)
      let prescriptionAge = rdAges.length > 0 ? Math.min(...rdAges) : 6;
      let useL = false;
      let startPrescription = 0;

      if (currentRef && typeof currentRef.age === "number") {
        prescriptionAge = currentRef.age;
        if (eyeCode != null) {
          const c = String(eyeCode).toUpperCase();
          useL = c === "L" || c === "LE";
        } else {
          useL =
            currentRef.l != null && currentRef.r != null
              ? currentRef.l < currentRef.r
              : currentRef.l != null;
        }
        startPrescription = (useL ? currentRef.l : currentRef.r) ?? 0;
      } else {
        const startData = byAge[0];
        if (eyeCode != null) {
          const c = String(eyeCode).toUpperCase();
          useL = c === "L" || c === "LE";
        } else {
          const last = predictiveData[predictiveData.length - 1];
          const left = last.l ?? 0;
          const right = last.r ?? 0;
          useL = left < right;
        }
        startPrescription = (useL ? startData.l : startData.r) ?? 0;
      }

      const rate =
        (predictiveData[predictiveData.length - 1] || {}).rate ?? 1.0;

      // Kotlin: value = if (eyeCode == "L") rd.l ?: 0.0 else rd.r ?: 0.0; realDataset.add(ChartPoint(rd.age, value))
      // Filter out null/undefined values, ensure unique ages (take first occurrence), and sort by age
      const seenAges = new Set();
      const realData = byAge
        .filter(rd => {
          const value = useL ? rd.l : rd.r;
          if (value === null || value === undefined) return false;
          if (seenAges.has(rd.age)) return false; // Skip duplicate ages
          seenAges.add(rd.age);
          return true;
        })
        .map(rd => ({
          x: rd.age,
          y: (useL ? rd.l : rd.r) ?? 0
        }))
        .sort((a, b) => a.x - b.x); // Ensure sorted by age

      const maxAge = Math.min(17, prescriptionAge + 10);
      // Age range includes prescriptionAge (starting point) and goes up to maxAge
      const ageRange = Array.from(
        { length: maxAge - prescriptionAge + 1 },
        (_, i) => prescriptionAge + i
      );

      // Kotlin: treatments = listOf(30, 40, 50, 60, 70, 0); label = if (mod == 0) "No Treatment" else "$mod%"
      // getPrescriptionAtAge(startPrescription, prescriptionAge, age, slowing, 1.0, submittedData.last().rate); round(*100)/100
      // Treatment curves should start from the current exam point (prescriptionAge, startPrescription)
      const treatments = [30, 40, 50, 60, 70, 0];
      const treatmentDatasets = treatments
        .filter(mod => selectedItems.selectedTreatments.includes(mod))
        .map(mod => {
          const slowing = mod === 0 ? 1.0 : (100.0 - mod) / 100.0;
          const label = mod === 0 ? "No Treatment" : `${mod}%`;
          // Treatment curves start from prescriptionAge (current exam point)
          // Include the starting point so curves visually connect to Real Patient Data
          const data = ageRange.map(age => ({
            x: age,
            y:
              Math.round(
                getPrescriptionAtAge(
                  startPrescription,
                  prescriptionAge,
                  age,
                  slowing,
                  1.0,
                  rate
                ) * 100
              ) / 100.0
          }));
          return {
            label,
            data,
            borderWidth: 2,
            borderColor: getTreatmentColor(mod),
            backgroundColor: getTreatmentColor(mod),
            fill: false,
            pointRadius: context => {
              // Hide point at the starting age (prescriptionAge) so it connects to real data
              return context.parsed.x === prescriptionAge ? 0 : 4;
            },
            pointBackgroundColor: getTreatmentColor(mod),
            pointBorderColor: getTreatmentColor(mod),
            pointBorderWidth: 1
          };
        });

      // Kotlin: datasets.add(Real Patient Data) then treatments.forEach { datasets.add(...) }
      // Real Patient Data should be added first so treatment curves can connect to it
      const datasets = [];
      if (selectedItems.includeRealPatientData && realData.length > 0) {
        datasets.push({
          label: "Real Patient Data",
          data: realData,
          borderWidth: 3,
          borderColor: "#EC6C84",
          fill: false,
          pointRadius: realData.length === 1 ? 4 : 0, // Show point if only one data point
          pointBackgroundColor: "#EC6C84",
          pointBorderColor: "#EC6C84",
          pointBorderWidth: 2
        });
      }
      datasets.push(...treatmentDatasets);

      // Create chart
      const chart = new Chart(ctx, {
        type: "line",
        data: {
          datasets
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false }
          },
          scales: {
            x: {
              type: "linear",
              position: "bottom",
              min: prescriptionAge,
              max: maxAge,
              title: {
                display: true,
                text: "Age (years)",
                font: { size: 25 }
              },
              ticks: {
                font: { size: 20 },
                stepSize: 1
              },
              grid: {
                display: true,
                color: "rgba(0,0,0,0.12)"
              }
            },
            y: {
              reverse: true,
              title: {
                display: true,
                text: "Refractive Error (D)",
                font: { size: 25 }
              },
              ticks: { font: { size: 20 } },
              grid: {
                display: true,
                color: "rgba(0,0,0,0.12)"
              }
            }
          }
        }
      });

      // Kotlin/GraphUtil: draw circle+label only on the LAST point of Real Patient Data (labels.getOrNull(0) = eye)
      const eyeLabel = useL ? "LE" : "RE";

      setTimeout(() => {
        ctx.save();
        ctx.globalCompositeOperation = "destination-over";
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();

        if (selectedItems.includeRealPatientData) {
          const idxReal = chart.data.datasets.findIndex(
            ds => ds.label === "Real Patient Data"
          );
          if (idxReal >= 0 && realData.length > 0) {
            const meta = chart.getDatasetMeta(idxReal);
            const last = meta.data[meta.data.length - 1];
            if (last) {
              const x = last.x;
              const y = last.y;
              const radius = 14;
              ctx.fillStyle = "#FFFFFF";
              ctx.beginPath();
              ctx.arc(x, y, radius, 0, 2 * Math.PI);
              ctx.fill();
              ctx.strokeStyle = "#EC6C84";
              ctx.lineWidth = 2.5;
              ctx.beginPath();
              ctx.arc(x, y, radius, 0, 2 * Math.PI);
              ctx.stroke();
              ctx.fillStyle = "#000000";
              ctx.font = "bold 11px Arial";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(eyeLabel, x, y);
            }
          }
        }

        const imageData = canvas.toDataURL("image/png");
        chart.destroy();
        if (container && container.parentNode) {
          document.body.removeChild(container);
        }
        resolve(imageData);
      }, 500);
    } catch (error) {
      if (container && container.parentNode) {
        document.body.removeChild(container);
      }
      reject(error);
    }
  });
};

// Helper functions
// Kotlin: slowing = (100-mod)/100; 70% => 0.3 = slowest progression (best/top curve); 30% => 0.7; 0 => 1.0 (worst).
// Map so Defocus = 70% (slowest) and Atropine 0.01% = 30%. Legend order matches dataset order: 30%, 40%, 50%, 60%, 70%, 0.
const getTreatmentColor = mod => {
  const colors = {
    0: "#C8CACE", // Grey - No treatment
    30: "#569FE4", // Sky blue - Atropine 0.01% (30% control = more progression than 70%)
    40: "#F1A253", // Orange/tan - Anti-Myopia Orthokeratology
    50: "#F6CE69", // Light yellow - Atropine 0.025%
    60: "#6BBDBE", // Teal - Anti-Myopia soft contacts
    70: "#74140B" // Dark reddish-brown - Defocus Segment Spectacle lens (70% control = slowest progression, best)
  };
  return colors[mod] || "#CCCCCC";
};
